class ProductMediaGallery extends HTMLElement {
  connectedCallback() {
    this.cacheElements();
    this.initSwiper();
    this.setupModalTriggers();
    this.setupVariantChangeListener();

    // Check if variant groups are enabled
    if (this.getAttribute('data-enable-variant-groups') === 'true') {
      this.variantGroupUpdate();
    }
  }

  disconnectedCallback() {
    this.swiper.destroy();
  }

  cacheElements() {
    this.swiperContainer = this.querySelector('.swiper');
    this.variantId = this.getAttribute('data-variant-id');
    this.featuredImage = this.querySelector('.product-media-gallery__featured-image img');
    this.images = Array.from(this.querySelectorAll('.swiper-slide'));
  }

  initSwiper() {
    this.swiper = new Swiper(this.swiperContainer, {
      slidesPerView: 4,
      spaceBetween: 10,
      updateOnWindowResize: true,
      autoHeight: true,
      watchSlidesProgress: true,
      loop: false,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      pagination: {
        el: this.swiperContainer.querySelector('.swiper-pagination'),
        clickable: true,
      },
      breakpoints: {
        320: {
          slidesPerView: 1,
          spaceBetween: 10,
        },
        1024: {
          slidesPerView: 4,
          spaceBetween: 10,
        },
      },
      on: {
        // Handle any slide change, whether from navigation or direct click
        slideChange: () => {
          // Don't update active state when using navigation
          if (
            this.swiper.navigation &&
            (this.swiper.navigation.nextEl === document.activeElement ||
              this.swiper.navigation.prevEl === document.activeElement)
          ) {
            return;
          }
        },
        // Remove Swiper's auto-added active classes after navigation
        transitionEnd: () => {
          const slides = this.querySelectorAll('.swiper-slide');
          slides.forEach((slide) => {
            if (!slide.classList.contains('is-active')) {
              slide.classList.remove('swiper-slide-active');
              slide.classList.remove('swiper-slide-thumb-active');
            }
          });
        },
      },
    });

    // Add click handlers for navigation buttons
    const navButtons = this.querySelectorAll('.swiper-button-next, .swiper-button-prev');
    navButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        // Don't update active state when using navigation
        const slides = this.querySelectorAll('.swiper-slide');
        slides.forEach((slide) => {
          if (!slide.classList.contains('is-active')) {
            slide.classList.remove('swiper-slide-active');
            slide.classList.remove('swiper-slide-thumb-active');
          }
        });
      });
    });

    // Update click handler for thumbnails
    this.swiper.slides.forEach((slide) => {
      const imageContainer = slide.querySelector('.product-media-gallery__image');
      if (imageContainer) {
        imageContainer.addEventListener('click', (event) => {
          // Only handle actual thumbnail clicks
          if (!event.target.closest('.swiper-button-next, .swiper-button-prev')) {
            const clickedImage = slide.querySelector('img');
            if (clickedImage && this.featuredImage) {
              this.featuredImage.src = clickedImage.src;
              if (clickedImage.srcset) {
                this.featuredImage.srcset = clickedImage.srcset;
              }
            }
            this.setActiveSlide(slide);
          }
        });
      }
    });

    // Set initial active slide
    if (this.swiper.slides.length > 0) {
      this.setActiveSlide(this.swiper.slides[0]);
    }
  }

  setActiveSlide(activeSlide) {
    if (!activeSlide) return;

    // Remove active classes from ALL possible elements that could have them
    const allSlides = this.querySelectorAll('.swiper-slide');
    allSlides.forEach((slide) => {
      slide.classList.remove('is-active');
      slide.classList.remove('swiper-slide-active');
      slide.classList.remove('swiper-slide-thumb-active');
    });

    // Add active class to the current slide
    activeSlide.classList.add('is-active');

    // Also update the featured image when setting active slide
    const activeImage = activeSlide.querySelector('img');
    if (activeImage && this.featuredImage) {
      this.featuredImage.src = activeImage.src;
      if (activeImage.srcset) {
        this.featuredImage.srcset = activeImage.srcset;
      }
    }

    // Ensure swiper's internal state is consistent
    const slideIndex = Array.from(this.swiper.slides).indexOf(activeSlide);
    if (slideIndex >= 0) {
      this.swiper.activeIndex = slideIndex;
      this.swiper.updateSlidesClasses();
    }
  }

  variantGroupUpdate() {
    const startIndex = this.images.findIndex((image) => image.dataset.variantId === this.variantId);

    const endIndex = this.images.slice(startIndex + 1).findIndex((image) => image.hasAttribute('data-variant-id'));

    const finalEndIndex = endIndex === -1 ? this.images.length : startIndex + 1 + endIndex;

    this.images.forEach((image, index) => {
      const slide = image.closest('.swiper-slide');
      slide.style.display = index >= startIndex && index < finalEndIndex ? 'block' : 'none';
    });

    // Update swiper after changing visibility
    this.swiper.update();
  }

  setupModalTriggers() {
    // Featured image click handler (desktop)
    const featuredImage = this.querySelector('.product-media-gallery__featured-image');
    if (featuredImage) {
      featuredImage.style.cursor = 'pointer';
      featuredImage.addEventListener('click', () => {
        this.openModal();
      });
    }

    // Mobile slide click handler
    const handleResize = () => {
      const isMobile = window.innerWidth < 1024; // Match the CSS breakpoint

      // Remove existing click listener if any
      if (this._modalClickHandler) {
        this.swiperContainer.removeEventListener('click', this._modalClickHandler);
        this._modalClickHandler = null;
      }

      // Only add click handler on mobile
      if (isMobile) {
        this._modalClickHandler = () => this.openModal();
        this.swiperContainer.addEventListener('click', this._modalClickHandler);
      }
    };

    // Initial setup
    handleResize();

    // Update on resize
    window.addEventListener('resize', handleResize);
  }

  openModal() {
    const modal = document.querySelector('product-modal');
    if (modal) {
      modal.show(this);
    }
  }

  setupVariantChangeListener() {
    document.addEventListener('variant:changed', (event) => {
      const variant = event.detail.variant;
      if (!variant) return;

      // Update the variant ID attribute
      this.setAttribute('data-variant-id', variant.id);
      this.variantId = variant.id;

      if (variant.featured_image) {
        // Update featured image
        if (this.featuredImage) {
          this.featuredImage.src = variant.featured_image.src;
          if (variant.featured_image.srcset) {
            this.featuredImage.srcset = variant.featured_image.srcset;
          }
        }

        // Find and update the variant slide
        const variantSlide = this.querySelector(`.swiper-slide[data-variant-id="${variant.id}"]`);
        if (variantSlide) {
          const variantImage = variantSlide.querySelector('img');
          if (variantImage) {
            variantImage.src = variant.featured_image.src;
            if (variant.featured_image.srcset) {
              variantImage.srcset = variant.featured_image.srcset;
            }
          }

          // Set this slide as active
          this.setActiveSlide(variantSlide);

          // Update swiper to show this slide
          const slideIndex = Array.from(this.swiper.slides).indexOf(variantSlide);
          if (slideIndex >= 0) {
            this.swiper.slideTo(slideIndex);
          }
        }

        // If variant groups are enabled, update the gallery
        if (this.getAttribute('data-enable-variant-groups') === 'true') {
          this.variantGroupUpdate();
        }

        // Update swiper after changes
        this.swiper.update();
      }
    });
  }
}

customElements.define('product-media-gallery', ProductMediaGallery);
